/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import { Table } from "reactstrap";
import { extractMetaProperties } from "../../../../../actions/Layers/util";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { RootState } from "../../../../../reducers";
import { useTenantDefectDefinitions } from "../../../../Forms/useManagedOptionsHook";
import { FOOTER_TYPES } from "../../../Tenants/Forms/FieldConfigForm";

export default function FruitTable({ fruits, check, layer, do_print }) {
    const config = useConfig();
    const user = useSelector<RootState, RootState["auth"]["user"]>((state) => state.auth.user);
    const tenantDefectDefinitions = useTenantDefectDefinitions(layer.fruit_type);

    const [headers, rows, footers] = getFruitTableData({ layer, check, fruits, config, user, tenantDefectDefinitions });

    return <Table size="sm" responsive={!do_print} borderless={true}>
        <thead>
            <tr>{headers.map((i, index) => <th key={index} className="text-muted text-start">{i}</th>)}</tr>
        </thead>
        <tbody>
            {rows.map((fields, rowIndex) => <tr key={rowIndex}>
                {fields.map((field, colIndex) => <td key={colIndex} className="text-capitalize text-start align-middle">{field.display_component()}</td>)}
            </tr>)}
        </tbody>
        <tbody>
            <tr>
                {footers.map((i, index) => <td key={index} className="fw-bold text-start">{i}</td>)}
            </tr>
        </tbody>
    </Table>;
}


export function getFruitTableData({ layer, check, fruits, config, user, tenantDefectDefinitions }) {
    const location = config.get_check_type_config(layer, check);
    const fields = location?.summary_fruit_fields_v2 || [];

    const extractedFruit = extractMetaProperties(fruits);

    const rows = extractedFruit.map((fruit) => fields.map(
        (i) => i.clone().set_value({
            config,
            layer,
            check,
            fruit,
            user,
            tenantDefectDefinitions
        })
    ));

    const headers = fields.map((i) => i.label);
    const footers = fields.map((field) => {
        const footerType = field?.field_config?.footer;

        if (!footerType) return null;

        const values = extractedFruit
            .map((fruit) => Number(fruit[field.fieldname_getter]))
            .filter((val) => !Number.isNaN(val) && val !== null && val !== undefined);

        if (values.length === 0) return null;

        field = field.clone().set_option("string_when_empty", "");

        switch (footerType) {
        case FOOTER_TYPES.average: {
            const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
            return field.formatted_value(avg);
        }
        case FOOTER_TYPES.median: {
            const sorted = [...values].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            const median = sorted.length % 2 !== 0
                ? sorted[mid]
                : (sorted[mid - 1] + sorted[mid]) / 2;
            return field.formatted_value(median);
        }
        case FOOTER_TYPES.sum: {
            const sum = values.reduce((sum, val) => sum + val, 0);
            return field.formatted_value(sum);
        }
        case FOOTER_TYPES.count: {
            return values.length;
        }
        case FOOTER_TYPES.range: {
            const min = Math.min(...values);
            const max = Math.max(...values);
            return `${field.formatted_value(min)} - ${field.formatted_value(max)}`;
        }

        case FOOTER_TYPES.mode: {
            const { mode } = values.reduce(
                (acc, val) => {
                    acc.frequency[val] = (acc.frequency[val] || 0) + 1;
                    if (acc.frequency[val] > acc.maxFreq) {
                        acc.maxFreq = acc.frequency[val];
                        acc.mode = val;
                    }
                    return acc;
                },
                { frequency: {}, maxFreq: 0, mode: null }
            );
            return field.formatted_value(mode);
        }
        default:
            return null;
        }
    });


    return [headers, rows, footers];
}
